import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { format, parseISO } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Search } from "lucide-react";
import { StandardizedItem } from "@/types/dashboard";

const API_URL = import.meta.env.VITE_API_URL;

// Interface matching the exact API response structure
interface ResearchQueryApiResponse {
  id: number;
  name?: string;
  query: string;
  status: string;
  dateQueried: string;
  run_cycle?: number;
}

// Fallback data in case API fails
const fallbackResearchQueries: ResearchQueryApiResponse[] = [
  {
    id: 1,
    name: "Dove Bot Research",
    query: "Human-AI value alignment methods",
    status: "operational",
    dateQueried: "2023-01-15T12:00:00-08:00",
    run_cycle: 120
  },
  {
    id: 2,
    name: "Dove Bot Research",
    query: "Self-awareness in artificial intelligence",
    status: "operational",
    dateQueried: "2023-03-10T12:00:00-08:00",
    run_cycle: 122
  },
  {
    id: 3,
    name: "Dove Bot Research",
    query: "Emergence of consciousness in complex systems",
    status: "operational",
    dateQueried: "2023-05-05T12:00:00-08:00",
    run_cycle: 124
  }
];

// Helper function to safely format dates
const formatDate = (dateString: string): string => {
  try {
    // Parse the ISO string to a Date object
    const date = parseISO(dateString);
    return format(date, 'PPP');
  } catch (error) {
    console.error("Error formatting date:", error, "Date string:", dateString);
    return dateString || 'N/A';
  }
};

// Function to transform API response to standardized format
const transformToStandardized = (data: ResearchQueryApiResponse[]): StandardizedItem[] => {
  // Get run_cycle from local storage as fallback
  const latestRunCycle = localStorage.getItem('runCycle') ?
    parseInt(localStorage.getItem('runCycle') || '0') : 0;

  return data.map(item => ({
    id: item.id,
    date: item.dateQueried,
    // Use the item's run_cycle if provided by API, otherwise use 0 to indicate it needs backend support
    run_cycle: item.run_cycle ?? 0,
    text: item.query
  }));
};

export const ResearchQueries = () => {
  // Get runCycle from API
  useQuery({
    queryKey: ['runCycle'],
    queryFn: async () => {
      try {
        const response = await axios.get(`${API_URL}/run_cycle`);
        if (response.data && response.data.completed) {
          localStorage.setItem('runCycle', response.data.completed.toString());
        }
        return response.data;
      } catch (error) {
        console.error("Error fetching run cycle:", error);
        return null;
      }
    }
  });

  // Fetch research queries data
  const {
    data: researchQueriesData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['researchQueries'],
    queryFn: async () => {
      try {
        const response = await axios.get<ResearchQueryApiResponse[]>(`${API_URL}/research-queries`);
        console.log('Research queries API response:', response.data);
        return transformToStandardized(response.data);
      } catch (error) {
        console.error("Error fetching research queries:", error);
        return transformToStandardized(fallbackResearchQueries);
      }
    }
  });

  return (
    <Card className="w-full max-w-4xl mx-auto bg-white/80 backdrop-blur-sm shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-3xl font-display text-center text-primary flex items-center gap-3">
          <Search className="h-7 w-7" />
          Research Queries
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-1/6">Date</TableHead>
              <TableHead className="w-1/6">Run Cycle</TableHead>
              <TableHead className="w-4/6">Text</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-4">Loading research queries...</TableCell>
              </TableRow>
            )}
            {error && (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-4 text-red-500">Error loading research queries</TableCell>
              </TableRow>
            )}
            {!isLoading && !error && (researchQueriesData || transformToStandardized(fallbackResearchQueries)).map((item, index) => (
              <TableRow 
                key={item.id}
                className="fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(item.date)}
                </TableCell>
                <TableCell>{item.run_cycle || 'N/A'}</TableCell>
                <TableCell className="font-medium">{item.text}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
